import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";

import { useAuth } from "@/contexts/auth";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import AdminSidebar from "./AdminSidebar";
import { adminBadgeClasses } from "./adminStyles";
import { getAdminPageMeta } from "./adminNavigation";

interface AdminLayoutProps {
  children?: ReactNode;
  title: string;
  description?: string;
  loading?: boolean;
  hideHeader?: boolean;
  listingName?: string;
  listingStatus?: string;
  onBack?: () => void;
  actions?: ReactNode;
  headerContent?: ReactNode;
  contentClassName?: string;
}

const statusBadgeTone = (status: string) => {
  switch (status) {
    case "approved":
      return adminBadgeClasses.success;
    case "pending":
      return adminBadgeClasses.warning;
    case "rejected":
      return adminBadgeClasses.danger;
    default:
      return adminBadgeClasses.neutral;
  }
};

const AdminLayout = ({
  children,
  title,
  description,
  loading = false,
  hideHeader = false,
  listingName,
  listingStatus,
  onBack,
  actions,
  headerContent,
  contentClassName,
}: AdminLayoutProps) => {
  const location = useLocation();
  const { loading: authLoading, isAdminLoading } = useAuth();
  const routeMeta = getAdminPageMeta(location.pathname);

  const resolvedTitle = listingName ? `${title}: ${listingName}` : title;
  const resolvedDescription = description ?? routeMeta.description;
  const sectionLabel = routeMeta.section;

  if (authLoading || isAdminLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-gray-50 text-gray-800">
      <SidebarProvider defaultOpen>
        <AdminSidebar />
        <SidebarInset className="min-h-svh bg-gray-50">
          <div className="flex min-h-svh flex-col">
            <div className="flex-1 overflow-y-auto">
              <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 py-5 sm:px-6 lg:px-8">
                {!hideHeader ? (
                  <section className="border-b border-gray-200 pb-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-3">
                          <SidebarTrigger className="h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 md:hidden" />
                          {onBack ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 rounded-lg border-gray-200 text-gray-700 hover:border-whatsapp hover:text-whatsapp"
                              onClick={onBack}
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Back
                            </Button>
                          ) : null}
                          <span className="text-xs font-medium text-gray-500">{sectionLabel}</span>
                          {listingStatus ? (
                            <Badge className={cn("rounded-full border px-3 py-1 text-xs font-semibold", statusBadgeTone(listingStatus))}>
                              {listingStatus}
                            </Badge>
                          ) : null}
                        </div>
                        <h1 className="mt-3 text-2xl font-bold text-gray-800">{resolvedTitle}</h1>
                        <p className="mt-1 max-w-3xl text-sm text-gray-600">{resolvedDescription}</p>
                        {headerContent ? <div className="mt-3">{headerContent}</div> : null}
                      </div>
                      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
                    </div>
                  </section>
                ) : null}

                {loading ? (
                  <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-gray-200 bg-white shadow-sm">
                    <div className="flex items-center gap-3 text-sm font-medium text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Loading
                    </div>
                  </div>
                ) : (
                  <div className={cn("flex flex-col gap-5", contentClassName)}>{children}</div>
                )}
              </div>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};

export default AdminLayout;
